import React from 'react';

/** What a keystroke means to a navigable list, or null if it means nothing. */
export type ListKeyIntent =
  | { t: 'move'; to: number }
  | { t: 'select'; index: number }
  | { t: 'escape' };

/**
 * The list's key semantics in one place, so a consumer that owns focus
 * elsewhere — the command palette, whose focus stays in its combobox input and
 * so never routes keys through this component — resolves them identically
 * instead of reimplementing the wraparound arithmetic beside it.
 */
export const resolveListKey = (
  key: string,
  params: {
    itemCount: number;
    selectedIndex: number;
    /** Space selects only where it is not also a printable character. */
    selectOnSpace?: boolean;
  }
): ListKeyIntent | null => {
  const { itemCount, selectedIndex, selectOnSpace = false } = params;

  if (key === 'Escape') {
    return { t: 'escape' };
  }

  if (itemCount === 0) {
    return null;
  }

  switch (key) {
    case 'ArrowDown':
      return { t: 'move', to: (selectedIndex + 1) % itemCount };
    case 'ArrowUp':
      return { t: 'move', to: (selectedIndex - 1 + itemCount) % itemCount };
    case 'Enter':
      return { t: 'select', index: selectedIndex };
    case ' ':
      return selectOnSpace ? { t: 'select', index: selectedIndex } : null;
    default:
      return null;
  }
};

type KeyboardNavigableListProps = {
  itemCount: number;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  onSelectItem?: (index: number) => void | Promise<void>;
  onEscape?: () => void;
  autoFocus?: boolean;
  /**
   * Whether the wrapper is its own tab stop. Set false when something else owns
   * focus and forwards the keys — a combobox input driving the list through
   * `aria-activedescendant` — so the wrapper is not a second, dead tab stop.
   */
  focusable?: boolean;
  containerClassName?: string;
  children: (props: {
    listRef: React.RefObject<HTMLDivElement>;
    getItemProps: (
      index: number,
      options?: { id?: string }
    ) => React.HTMLAttributes<HTMLDivElement>;
    activeDescendantId?: string;
  }) => React.ReactNode;
};

const DEFAULT_CONTAINER_CLASS = 'outline-none';

export const KeyboardNavigableList: React.FC<KeyboardNavigableListProps> = ({
  itemCount,
  selectedIndex,
  setSelectedIndex,
  onSelectItem,
  onEscape,
  autoFocus = true,
  focusable = true,
  containerClassName,
  children,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const itemIdsRef = React.useRef<string[]>([]);

  const setIndex = React.useCallback(
    (updater: React.SetStateAction<number>) => {
      setSelectedIndex((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (value: number) => number)(prev)
            : updater;
        if (Number.isNaN(next) || !Number.isFinite(next)) {
          return prev;
        }
        return next;
      });
    },
    [setSelectedIndex]
  );

  const scrollSelectedIntoView = React.useCallback(() => {
    const container = listRef.current;
    if (!container) {
      return;
    }

    const selectedElement = container.querySelector<HTMLElement>(
      `[data-index="${selectedIndex}"]`
    );

    if (!selectedElement) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = selectedElement.getBoundingClientRect();
    const padding = 8;

    if (elementRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - elementRect.top + padding;
    } else if (elementRect.bottom > containerRect.bottom) {
      container.scrollTop +=
        elementRect.bottom - containerRect.bottom + padding;
    }
  }, [selectedIndex]);

  React.useEffect(() => {
    if (!autoFocus) {
      return;
    }
    containerRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  React.useEffect(() => {
    if (itemCount === 0) {
      setSelectedIndex(0);
      return;
    }

    setSelectedIndex((prev) => {
      if (prev >= itemCount) {
        return itemCount - 1;
      }
      if (prev < 0) {
        return 0;
      }
      return prev;
    });
  }, [itemCount, setSelectedIndex]);

  React.useEffect(() => {
    itemIdsRef.current.length = itemCount;
  }, [itemCount]);

  React.useEffect(() => {
    scrollSelectedIntoView();
  }, [scrollSelectedIntoView, selectedIndex]);

  const handleSelect = React.useCallback(
    (index: number) => {
      if (onSelectItem) {
        void onSelectItem(index);
      }
    },
    [onSelectItem]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const intent = resolveListKey(event.key, {
        itemCount,
        selectedIndex,
        selectOnSpace: true,
      });

      if (intent === null) {
        return;
      }

      event.preventDefault();

      switch (intent.t) {
        case 'move':
          setIndex(intent.to);
          break;
        case 'select':
          handleSelect(intent.index);
          break;
        case 'escape':
          onEscape?.();
          break;
      }
    },
    [handleSelect, itemCount, onEscape, selectedIndex, setIndex]
  );

  const getItemProps = React.useCallback(
    (index: number, options?: { id?: string }) => {
      const id = options?.id ?? `keyboard-navigable-item-${index}`;
      itemIdsRef.current[index] = id;

      const handleClick = () => {
        setSelectedIndex(index);
        handleSelect(index);
      };

      return {
        id,
        'data-index': index,
        role: 'option',
        'aria-selected': index === selectedIndex,
        // `pointermove`, not `mouseenter`. `mouseenter` also fires when the
        // list scrolls a row under a parked cursor, so arrowing down had the
        // selection yanked straight back to wherever the mouse happened to be —
        // and the scroll-into-view that followed slid the next row under the
        // cursor, feeding the loop. `pointermove` only fires on real movement.
        onPointerMove: () => {
          setSelectedIndex(index);
        },
        onClick: handleClick,
      } as React.HTMLAttributes<HTMLDivElement>;
    },
    [handleSelect, selectedIndex, setSelectedIndex]
  );

  const activeDescendantId = itemIdsRef.current[selectedIndex];
  const containerClass = containerClassName
    ? `${DEFAULT_CONTAINER_CLASS} ${containerClassName}`.trim()
    : DEFAULT_CONTAINER_CLASS;

  return (
    <div
      ref={containerRef}
      tabIndex={focusable ? 0 : -1}
      role="presentation"
      onKeyDown={handleKeyDown}
      className={containerClass}
    >
      {children({
        listRef,
        getItemProps,
        activeDescendantId,
      })}
    </div>
  );
};
